/**
 * Busca appointments de Pronto Atendimento do profissional (hoje).
 * Filtro client-side por appointmentType=prontoatendimento.
 */
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { oids } from "@/configs/Settings";
import { useClinicalRegisterContext } from "@/contexts/ClinicalRegisterContext";
import { useLocationContext } from "@/contexts/LocationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { GetAppointment } from "@/services/fhir";
import {
  mapBundleToProntoQueueRows,
  type ProntoQueueRow,
} from "./mapProntoAppointmentToQueueItem";

export function useProntoAtendimentoQueue(options?: {
  enabled?: boolean;
  /** Se informado, busca a fila desse médico (visão clínica). Senão, o logado. */
  professionalActor?: string;
}) {
  const enabled = options?.enabled !== false;
  const professionalActorOverride = options?.professionalActor;
  const { user } = useSessionContext();
  const { location, locationLoading } = useLocationContext();
  const { inProgress } = useClinicalRegisterContext();

  const [rows, setRows] = useState<ProntoQueueRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);

  const refetch = useCallback(() => {
    setRefetchToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (locationLoading) return;

    async function fetchQueue() {
      const locationId = location
        ?.map((e) => e.resource?.id)
        ?.filter(Boolean)
        ?.join(",");

      const professionalActor =
        professionalActorOverride ||
        (user?.username
          ? `Practitioner/${oids.cpf}-${user.username}`
          : undefined);

      if (!locationId || !professionalActor) {
        setRows([]);
        return;
      }

      setLoading(true);
      try {
        const today = dayjs().format("YYYY-MM-DD");
        // Mesmo espírito de ProfessionalSchedule: booked/pending/proposed + include patient
        const bundle = await GetAppointment({
          scheduling: true,
          includePatient: true,
          includeLocation: true,
          status: ["booked", "pending", "proposed", "arrived"],
          actor: locationId,
          startDate: today,
          endDate: today,
          sort: "date",
          professionalActor,
        });

        // Filtro client-side: só prontoatendimento (API pode não filtrar por type)
        setRows(mapBundleToProntoQueueRows(bundle, inProgress));
      } catch (error) {
        console.error("useProntoAtendimentoQueue", error);
        toast.error("Não foi possível carregar os próximos pacientes.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    }

    void fetchQueue();
  }, [
    enabled,
    location,
    locationLoading,
    user?.username,
    professionalActorOverride,
    inProgress,
    refetchToken,
  ]);

  return { rows, loading, refetch };
}
